import {
  USER_LOGIN_REQUEST,
  USER_LOGIN_SUCCESS,
  USER_LOGIN_FAIL,
  USER_LOGOUT,
  USER_REGISTER_REQUEST,
  USER_REGISTER_SUCCESS,
  USER_REGISTER_FAIL,
  USER_DETAILS_REQUEST,
  USER_DETAILS_SUCCESS,
  USER_DETAILS_FAIL,
  USER_DETAILS_RESET,
  USER_UPDATE_PROFILE_REQUEST,
  USER_UPDATE_PROFILE_SUCCESS,
  USER_UPDATE_PROFILE_FAIL,
  USER_UPDATE_PROFILE_RESET,
  USER_UPDATE_PROFILE_IMAGE_REQUEST,
  USER_UPDATE_PROFILE_IMAGE_SUCCESS,
  USER_UPDATE_PROFILE_IMAGE_FAIL,
  USER_UPDATE_PROFILE_IMAGE_RESET,
  USER_FOLLOW_REQUEST,
  USER_FOLLOW_SUCCESS,
  USER_FOLLOW_FAIL,
  USER_FOLLOW_RESET,
  USER_UNFOLLOW_REQUEST,
  USER_UNFOLLOW_SUCCESS,
  USER_UNFOLLOW_FAIL,
  USER_UNFOLLOW_RESET,
  USER_LIST_REQUEST,
  USER_LIST_SUCCESS,
  USER_LIST_FAIL,
  USER_LIST_RESET,
  USER_DELETE_REQUEST,
  USER_DELETE_SUCCESS,
  USER_DELETE_FAIL,
  USER_UPDATE_REQUEST,
  USER_UPDATE_SUCCESS,
  USER_UPDATE_FAIL,
  USER_UPDATE_RESET,
  USER_VALIDATE_PROFILE_REQUEST,
  USER_VALIDATE_PROFILE_SUCCESS,
  USER_VALIDATE_PROFILE_FAIL,
  USER_VALIDATE_PROFILE_RESET,
  USER_SEND_MAIL_PROFILE_REQUEST,
  USER_SEND_MAIL_PROFILE_SUCCESS,
  USER_SEND_MAIL_PROFILE_FAIL,
  USER_SEND_MAIL_PROFILE_RESET,
  USER_CHANGE_PASS_PROFILE_REQUEST,
  USER_CHANGE_PASS_PROFILE_SUCCESS,
  USER_CHANGE_PASS_PROFILE_FAIL,
  USER_CHANGE_PASS_PROFILE_RESET,
  USER_SEND_MAIL_CHANGE_PASS_REQUEST,
  USER_SEND_MAIL_CHANGE_PASS_SUCCESS,
  USER_SEND_MAIL_CHANGE_PASS_FAIL,
  USER_SEND_MAIL_CHANGE_PASS_RESET,

  USER_TOKEN_REFRESH,
} from '../constants/userConstants';


const mergeUserInfo = (currentUserInfo = {}, nextUserInfo = {}) => {
  const access =
    nextUserInfo?.access ||
    currentUserInfo?.access;

  return {
    ...currentUserInfo,
    ...nextUserInfo,
    access,
    refresh: nextUserInfo?.refresh || currentUserInfo?.refresh,
  };
};

export const userLoginReducers = (state = {}, action) => {
  switch (action.type) {
    case USER_LOGIN_REQUEST:
      // INVARIANT (B2 — load-bearing for auth/WS continuity):
      //   USER_LOGIN_REQUEST preserves the existing authenticated session
      //   intentionally. It represents an IN-FLIGHT AUTH ATTEMPT, NOT a
      //   session reset action. An already-authenticated user who triggers
      //   a re-login attempt (e.g. account-switch flow, accidental form
      //   re-submit) MUST NOT transiently lose their userInfo.
      //
      //   Concrete consequences of preserving userInfo here:
      //     - selectAccessToken stays truthy → App.js WS lifecycle does
      //       not fire wsManager.disconnect mid-attempt
      //     - selectIsAuthed stays true → Navbar/ChatLayout do not flicker
      //       to logged-out UI mid-attempt
      //     - localStorage.userInfo is untouched (no reducer write to it
      //       on REQUEST anyway, but the in-memory parity is preserved)
      //
      //   error is reset to undefined to clear any prior FAIL message
      //   ahead of the new attempt's outcome.
      //
      //   Reset to a clean slate happens ONLY in USER_LOGOUT (explicit
      //   user-initiated logout) — never as a side effect of a request
      //   lifecycle action.
      return { ...state, loading: true, error: undefined };

    case USER_LOGIN_SUCCESS:
      return {
        loading: false,
        success: true,
        userInfo: mergeUserInfo(state.userInfo, action.payload),
      };

    case USER_LOGIN_FAIL:
      // INVARIANT (B2): same as USER_LOGIN_REQUEST — preserve existing
      // session on a failed re-login attempt. The user remains
      // authenticated; only the new attempt failed. Forced logout on
      // attempt failure would force-redirect to /login, drop the WS
      // socket, and discard the working session — exactly the
      // production-blocker behavior B2 is fixing.
      return { ...state, loading: false, error: action.payload };

    case USER_LOGOUT:
      return {};

    case USER_TOKEN_REFRESH:
      return {
        ...state,
        userInfo: mergeUserInfo(state.userInfo, {
          access: action.payload,
        }),
      }

    default:
      return state;
  }
};

export const userRegisterReducers = (state = {}, action) => {
  switch (action.type) {
    case USER_REGISTER_REQUEST:
      return { loading: true };

    case USER_REGISTER_SUCCESS:
      return { loading: false, success: true, userInfo: action.payload };

    case USER_REGISTER_FAIL:
      return { loading: false, error: action.payload };

    case USER_LOGOUT:
      return {};

    default:
      return state;
  }
};

export const userDetailsReducers = (
  state = { user: null, loading: false, success: false, error: undefined, requestId: undefined },
  action,
) => {
  switch (action.type) {
    case USER_DETAILS_REQUEST:
      return {
        loading: true,
        success: false,
        user: null,
        error: undefined,
        requestId: action.meta?.requestId,
      };

    case USER_DETAILS_SUCCESS:
      if (action.meta?.requestId != null) {
        if (action.meta.requestId !== state.requestId) {
          return state;
        }
      }
      return { loading: false, success: true, user: action.payload, error: undefined, requestId: state.requestId };

    case USER_DETAILS_FAIL:
      if (action.meta?.requestId != null) {
        if (action.meta.requestId !== state.requestId) {
          return state;
        }
      }
      return { loading: false, success: false, user: null, error: action.payload, requestId: state.requestId };

    case USER_DETAILS_RESET:
      return { loading: false, error: undefined, success: false, user: null, requestId: undefined };

    default:
      return state;
  }
};

export const userUpdateProfileReducers = (state = {}, action) => {
  switch (action.type) {
    case USER_UPDATE_PROFILE_REQUEST:
      // Clear prior fieldErrors / error on every new attempt.
      return { loading: true };

    case USER_UPDATE_PROFILE_SUCCESS:
      return { loading: false, success: true, userInfo: action.payload };

    case USER_UPDATE_PROFILE_FAIL: {
      const payload = action.payload;
      if (typeof payload === "string") {
        return { loading: false, error: payload };
      }
      if (payload && typeof payload === "object") {
        return {
          loading: false,
          error: payload.message || "Не вдалося оновити профіль.",
          fieldErrors: payload.fieldErrors || {},
        };
      }
      return { loading: false, error: "Не вдалося оновити профіль." };
    }

    case USER_UPDATE_PROFILE_RESET:
      return {};

    default:
      return state;
  }
};

export const userUpdateProfileImageReducers = (state = {}, action) => {
  switch (action.type) {
    case USER_UPDATE_PROFILE_IMAGE_REQUEST:
      return { loading: true };

    case USER_UPDATE_PROFILE_IMAGE_SUCCESS:
      return { loading: false, success: true, userInfo: action.payload };

    case USER_UPDATE_PROFILE_IMAGE_FAIL:
      return { loading: false, error: action.payload };

    case USER_UPDATE_PROFILE_IMAGE_RESET:
      return {};

    default:
      return state;
  }
};

export const userFollowReducers = (state = {}, action) => {
  switch (action.type) {
    case USER_FOLLOW_REQUEST:
      return { loading: true };

    case USER_FOLLOW_SUCCESS:
      return { loading: false, success: true, userInfo: action.payload };

    case USER_FOLLOW_FAIL:
      return { loading: false, error: action.payload };

    case USER_FOLLOW_RESET:
      return { success: false, error: false }

    default:
      return state;
  }
};

export const userUnfollowReducers = (state = {}, action) => {
  switch (action.type) {
    case USER_UNFOLLOW_REQUEST:
      return { loading: true };

    case USER_UNFOLLOW_SUCCESS:
      return { loading: false, success: true, userInfo: action.payload };

    case USER_UNFOLLOW_FAIL:
      return { loading: false, error: action.payload };

    case USER_UNFOLLOW_RESET:
      return { success: false, error: false }

    default:
      return state;
  }
};

export const userListReducers = (state = { users: [] }, action) => {
  switch (action.type) {
    case USER_LIST_REQUEST:
      return { loading: true };

    case USER_LIST_SUCCESS:
      return { loading: false, success: true, users: action.payload };

    case USER_LIST_FAIL:
      return { loading: false, error: action.payload };

    case USER_LIST_RESET:
      return { users: [] };

    default:
      return state;
  }
};

export const userDeleteReducers = (state = {}, action) => {
  switch (action.type) {
    case USER_DELETE_REQUEST:
      return { loading: true };

    case USER_DELETE_SUCCESS:
      return { loading: false, success: true };

    case USER_DELETE_FAIL:
      return { loading: false, error: action.payload };

    default:
      return {};
  }
};

export const userUpdateReducers = (state = { user: {} }, action) => {
  switch (action.type) {
    case USER_UPDATE_REQUEST:
      return { loading: true };

    case USER_UPDATE_SUCCESS:
      return { loading: false, success: true };

    case USER_UPDATE_FAIL:
      return { loading: false, error: action.payload };

    case USER_UPDATE_RESET:
      return { user: {} };

    default:
      return state;
  }
};

export const userSendMailReducers = (state = {}, action) => {
  switch (action.type) {
    case USER_SEND_MAIL_PROFILE_REQUEST:
      return { loading: true, success: false };

    case USER_SEND_MAIL_PROFILE_SUCCESS:
      return { loading: false, success: true };

    case USER_SEND_MAIL_PROFILE_FAIL:
      return { loading: false, success: false, error: action.payload };

    case USER_SEND_MAIL_PROFILE_RESET:
      return { loading: false, success: false, error: null };

    default:
      return state;
  }
};

export const userSendMailChangePassReducers = (state = {}, action) => {
  switch (action.type) {
    case USER_SEND_MAIL_CHANGE_PASS_REQUEST:
      return { loading: true, success: false };

    case USER_SEND_MAIL_CHANGE_PASS_SUCCESS:
      return { loading: false, success: true };

    case USER_SEND_MAIL_CHANGE_PASS_FAIL:
      return { loading: false, success: false, error: action.payload };

    case USER_SEND_MAIL_CHANGE_PASS_RESET:
      return { loading: false, success: false, error: null };

    default:
      return state;
  }
};

export const validateUserProfileReducers = (state = {}, action) => {
  switch (action.type) {
    case USER_VALIDATE_PROFILE_REQUEST:
      return { loading: true };

    case USER_VALIDATE_PROFILE_SUCCESS:
      return { loading: false, success: true, userInfo: action.payload };

    case USER_VALIDATE_PROFILE_FAIL:
      return { loading: false, error: action.payload };

    case USER_VALIDATE_PROFILE_RESET:
      return {};

    default:
      return state;
  }
};

export const changePasswordUserReducers = (state = {}, action) => {
  switch (action.type) {
    case USER_CHANGE_PASS_PROFILE_REQUEST:
      return { loading: true };

    case USER_CHANGE_PASS_PROFILE_SUCCESS:
      return { loading: false, success: true };

    case USER_CHANGE_PASS_PROFILE_FAIL:
      return { loading: false, error: action.payload };

    case USER_CHANGE_PASS_PROFILE_RESET:
      return { loading: false, success: false, error: null };

    default:
      return state;
  }
};
